import {
  isExteriorOpeningCasingClipsFinishAnalyticFace,
  isExteriorOpeningUnitAnalyticFace,
  isFoundationSlab,
  isFoundationVolume,
  isWallExteriorFinishBRep,
} from "building-common/analyticEntity/buildingAnalyticEntitySelectors";
import type { Building } from "building-types/building/buildingTypes";
import type {
  BuildingAnalyticBRep,
  BuildingAnalyticEntity,
  BuildingAnalyticExtrusion,
  BuildingAnalyticFace,
} from "building-types/buildingAnalyticEntityTypes";
import {
  EXTERIOR_FINISH_TAG,
  EDGE,
  buildingGeomAttributeTagNew,
  type BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  EXTERIOR_FINISH_EDGE_ANGLE_ATTRIBUTE,
  EXTERIOR_FINISH_EDGE_GEOMETRY_TYPE_ATTRIBUTE,
  EXTERIOR_FINISH_EDGE_SIDE_ATTRIBUTE,
  EXTERIOR_FINISH_EDGE_TYPE_ATTRIBUTE,
  type ExteriorFinishEdgeOpeningSide,
} from "building-types/exteriorFinishEdgeAttributeTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { docEntityHandleIsGeneratedBuildingEntityHandle } from "doc/entity/docEntityHandleTools";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { aabb3Grow } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { BRep } from "geom/brep";
import { brepNew, faceArea, faceTopoNew, traverseFaceEdges, type Face } from "geom/brep";
import {
  computeBRepAdjacenciesForGroups,
  faceEdgeLine3Segment,
  type BRepAdjacency,
  type BRepAdjacencyCandidate,
  type BRepAdjacencyEdgeRef,
} from "geom/faceAdjacency";
import { line2Parameter } from "geom/line2";
import { line3Eval, line3Len, line3SegmentAABB3, type Line3Segment } from "geom/line3";
import {
  planeContainsPolyLine3Edges,
  planeCopy,
  planeEval,
  planeMoveAlongNormal,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
  planeSignedDistanceTo,
  polyLine2OnPlane,
} from "geom/plane";
import { polyLine2Edge, polyLine2EdgeCount, polyLine2EdgesOverlap, polyLine2Segment } from "geom/polyLine2";
import { polyLine3New, type PolyLine3 } from "geom/polyLine3";
import { vec3DistSquared } from "geom/vec3";
import {
  computeExteriorFinishEdgeAngleAndType,
  computeExteriorFinishEdgeOpeningSide,
} from "../generationTools/exteriorFinishEdgeAttributes";
import { addAnalyticLineSegment, addAnalyticPolyLine } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

/** The minimum length of a segment to be considered for adjacency */
const EDGE_LENGTH_THRESHOLD = 0.1;

/** The maximum distance between edge vertices for them to be considered overlapping in the plane of the primary face */
const EDGE_OVERLAP_TOLERANCE = 0.01;
const EDGE_OVERLAP_TOLERANCE_SQUARED = EDGE_OVERLAP_TOLERANCE * EDGE_OVERLAP_TOLERANCE;

/** The maximum angular deviation between tessellated edges for them to be considered collinear */
const EDGE_ANGLE_TOLERANCE = 0.01;

/** The minimum area of a face to be considered for adjacency */
const MIN_FACE_AREA_THRESHOLD = 0.1;

/** Search distance for foundation adjacency near open finish edges */
const FOUNDATION_SEARCH_DISTANCE = 1;

/**
 * The minimum overlap length with a foundation extrusion to be considered for adjacency.
 *
 * The value is pretty coarse, but eliminates some obvious false positives in the wild.
 *
 * This should only be needed for walls v1 where walls and the foundations below them are poorly associated.
 */
const FOUNDATION_MIN_OVERLAP_LENGTH = 1;

const adjacencyStyle = {
  color: 0xff8800,
  opacity: percentNew(0.7),
  lineWidth3d: worldInchesNew(1),
  lineWidth2d: worldInchesNew(1),
};

const exteriorFinishEdgeAdjacencyRules = [
  { queryGroups: ["FINISH"], targetGroups: ["FINISH", "OPENING"] },
  { queryGroups: ["FINISH", "OPENING"], targetGroups: ["CASING"] },
];

type ExteriorFinishEdgeAdjacencyCandidateKind = "FINISH" | "OPENING" | "CASING";

type ExteriorFinishEdgeAdjacencyCandidatesByKind = Record<
  ExteriorFinishEdgeAdjacencyCandidateKind,
  BRepAdjacencyEdgeRef[]
>;

type Interval = { readonly t0: number; readonly t1: number };

const select = makeBuildingSelector([
  "analyticWallEntities",
  "analyticDoorTrimEntities",
  "analyticWindowTrimEntities",
  "analyticFoundationEntities",
  "analyticFoundationSuperSystemEntities",
  "analyticFoundationHorizontalAssemblyEntities",
]);

const compare = makeGenerationStageComparator<typeof select>();

function generate(inputs: ReturnType<typeof select>): Pick<Building, "analyticExteriorFinishEdgeEntities"> {
  const analyticEntities: Table<BuildingAnalyticEntity> = {
    ...inputs.analyticFoundationEntities,
    ...inputs.analyticFoundationSuperSystemEntities,
    ...inputs.analyticFoundationHorizontalAssemblyEntities,
    ...inputs.analyticWallEntities,
    ...inputs.analyticDoorTrimEntities,
    ...inputs.analyticWindowTrimEntities,
  };
  const newEntities: RWTable<BuildingAnalyticEntity> = {};
  const breps: RWTable<BRep> = {};
  const candidatesByKind: ExteriorFinishEdgeAdjacencyCandidatesByKind = {
    FINISH: [],
    OPENING: [],
    CASING: [],
  };

  for (const entity of Object.values(analyticEntities)) {
    if (isWallExteriorFinishBRep(entity)) {
      addFinishCandidate(breps, candidatesByKind.FINISH, entity);
    } else if (isExteriorOpeningCasingClipsFinishAnalyticFace(entity)) {
      addOpeningCandidate(breps, candidatesByKind.CASING, entity);
    } else if (isExteriorOpeningUnitAnalyticFace(entity)) {
      addOpeningCandidate(breps, candidatesByKind.OPENING, entity);
    }
  }

  const coveredIntervalsByEdgeKey = new Map<string, Interval[]>();

  const toleranceOptions = {
    distanceTolerance: EDGE_OVERLAP_TOLERANCE,
    angleTolerance: EDGE_ANGLE_TOLERANCE,
    candidateLengthThreshold: EDGE_LENGTH_THRESHOLD,
  };

  const adjacencies = computeBRepAdjacenciesForGroups(
    breps,
    candidatesByKind,
    exteriorFinishEdgeAdjacencyRules,
    toleranceOptions,
  );
  const curvedProfileEdgeKeys = collectCurvedProfileEdgeKeys(analyticEntities, [
    ...candidatesByKind.OPENING,
    ...candidatesByKind.CASING,
  ]);

  const curvedParts: CurvedProfileAdjacencyPart[] = [];
  for (const adjacency of adjacencies) {
    recordCoverage(coveredIntervalsByEdgeKey, adjacency);
    const curvedPart = curvedProfileAdjacencyPart(analyticEntities, adjacency);
    if (curvedPart && curvedProfileEdgeKeys.has(edgeCandidateKey(curvedPart.source))) {
      curvedParts.push(curvedPart);
    } else {
      addEdgeAdjacency(newEntities, analyticEntities, adjacency, "STRAIGHT");
    }
  }
  addEdgeAdjacenciesCurved(newEntities, analyticEntities, curvedParts);

  for (const finishCandidate of candidatesByKind.FINISH) {
    addFoundationAdjacenciesForFinishEdge(
      newEntities,
      analyticEntities,
      breps,
      finishCandidate,
      coveredIntervalsByEdgeKey.get(edgeCandidateKey(finishCandidate)) ?? [],
      toleranceOptions,
    );
  }

  return {
    analyticExteriorFinishEdgeEntities: newEntities,
  };
}

function edgeCandidateKey(ref: BRepAdjacencyEdgeRef): string {
  return `${ref.id}:${ref.faceIndex}:${ref.loopIndex}:${ref.edgeIndex}`;
}

function recordCoverage(coveredIntervalsByEdgeKey: Map<string, Interval[]>, adjacency: BRepAdjacency): void {
  const { candidateA, candidateB } = adjacency;

  const candidateAKey = edgeCandidateKey(candidateA);
  appendFinishEdgeCoverage(coveredIntervalsByEdgeKey, candidateAKey, adjacency.t0, adjacency.t1);

  const candidateBKey = edgeCandidateKey(candidateB);
  const targetInterval = adjacencyIntervalOnCandidate(candidateB, adjacency.segment);
  if (targetInterval) {
    appendFinishEdgeCoverage(coveredIntervalsByEdgeKey, candidateBKey, targetInterval.t0, targetInterval.t1);
  }
}

function adjacencyIntervalOnCandidate(candidate: BRepAdjacency["candidateA"], segment: Line3Segment): Interval | null {
  const [x0, y0, x1, y1] = candidate.segment2;
  const overlapSegment = polyLine2Segment(planeProjectPolyLine3(candidate.face.plane, polyLine3New(segment)));
  const [startX, startY, endX, endY] = overlapSegment;
  const startT = line2Parameter(x0, y0, x1, y1, startX, startY);
  const endT = line2Parameter(x0, y0, x1, y1, endX, endY);
  if (startT === undefined || endT === undefined) {
    return null;
  }

  // Coverage intervals must be ascending because adjacent edges can have opposite parameter directions.
  return { t0: Math.min(startT, endT), t1: Math.max(startT, endT) };
}

function appendFinishEdgeCoverage(
  coveredIntervalsByEdgeKey: Map<string, Interval[]>,
  key: string,
  t0: number,
  t1: number,
): void {
  const coveredIntervals = coveredIntervalsByEdgeKey.get(key) ?? [];
  coveredIntervals.push({ t0, t1 });
  coveredIntervalsByEdgeKey.set(key, coveredIntervals);
}

function mergeEdgeIntervals(intervals: readonly Interval[], tolerance: number): Interval[] {
  if (intervals.length === 0) {
    return [];
  }

  const sorted = [...intervals].sort((a, b) => a.t0 - b.t0);
  const first = sorted[0];
  assertIsNotNullOrUndefined(first);
  const merged: Interval[] = [first];
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const previous = merged[merged.length - 1];
    assertIsNotNullOrUndefined(current);
    assertIsNotNullOrUndefined(previous);
    if (current.t0 <= previous.t1 + tolerance) {
      merged[merged.length - 1] = {
        t0: previous.t0,
        t1: Math.max(previous.t1, current.t1),
      };
    } else {
      merged.push(current);
    }
  }

  return merged;
}

function openEdgeIntervals(coveredIntervals: readonly Interval[], tolerance: number): Interval[] {
  if (coveredIntervals.length === 0) {
    return [{ t0: 0, t1: 1 }];
  }

  const openIntervals: Interval[] = [];
  const mergedCovered = mergeEdgeIntervals(coveredIntervals, tolerance);
  let openStart = 0;

  for (const { t0: coveredStart, t1: coveredEnd } of mergedCovered) {
    if (coveredStart > openStart + tolerance) {
      openIntervals.push({ t0: openStart, t1: coveredStart });
    }
    openStart = Math.max(openStart, coveredEnd);
  }

  if (openStart < 1 - tolerance) {
    openIntervals.push({ t0: openStart, t1: 1 });
  }

  return openIntervals;
}

function line3SegmentAtParams(segment: Line3Segment, t0: number, t1: number): Line3Segment {
  const [x0, y0, z0, x1, y1, z1] = segment;
  const [startX, startY, startZ] = line3Eval(x0, y0, z0, x1, y1, z1, t0);
  const [endX, endY, endZ] = line3Eval(x0, y0, z0, x1, y1, z1, t1);
  return [startX, startY, startZ, endX, endY, endZ];
}

function foundationSearchAABB(segment: Line3Segment): AABB3 {
  const aabb = line3SegmentAABB3(segment);
  aabb3Grow(aabb, FOUNDATION_SEARCH_DISTANCE, FOUNDATION_SEARCH_DISTANCE, FOUNDATION_SEARCH_DISTANCE);
  return aabb;
}

/**
 * This is not very precise and should not be necessary with walls v2 where
 * walls and foundations have more explicit associations.
 */
function findBestNearbyFoundation(
  segment: Line3Segment,
  analyticEntities: Table<BuildingAnalyticEntity>,
): BuildingAnalyticExtrusion | null {
  const searchAABB = foundationSearchAABB(segment);
  let bestFoundation: { readonly entity: BuildingAnalyticExtrusion; readonly overlapLength: number } | null = null;

  for (const handle of BuildingGenerationContext.indexSpatialSearch(searchAABB)) {
    if (!docEntityHandleIsGeneratedBuildingEntityHandle(handle)) {
      continue;
    }

    const entity = analyticEntities[handle.entity.id];
    if (!entity || !isFoundationAdjacencyCandidate(entity)) {
      continue;
    }

    const aabb = BuildingGenerationContext.indexNodeAABB(entity.docEntityHandle);
    if (!aabb) {
      continue;
    }

    // This creates a tighter tolerance for non-axis aligned segments, but again
    // this is should not be necessary for walls v2
    const overlapLength = line3SegmentAABBMaxAxisOverlap(segment, aabb);
    if (overlapLength <= FOUNDATION_MIN_OVERLAP_LENGTH) {
      continue;
    }

    if (
      !bestFoundation ||
      overlapLength > bestFoundation.overlapLength ||
      (overlapLength === bestFoundation.overlapLength && entity.id < bestFoundation.entity.id)
    ) {
      bestFoundation = { entity, overlapLength };
    }
  }

  return bestFoundation?.entity ?? null;
}

function isFoundationAdjacencyCandidate(entity: BuildingAnalyticEntity): entity is BuildingAnalyticExtrusion {
  return isFoundationVolume(entity) || isFoundationSlab(entity);
}

function line3SegmentAABBMaxAxisOverlap(segment: Line3Segment, aabb: AABB3): number {
  const segmentAABB = line3SegmentAABB3(segment);
  return Math.max(
    axisOverlapLength(segmentAABB.minX, segmentAABB.maxX, aabb.minX, aabb.maxX),
    axisOverlapLength(segmentAABB.minY, segmentAABB.maxY, aabb.minY, aabb.maxY),
    axisOverlapLength(segmentAABB.minZ, segmentAABB.maxZ, aabb.minZ, aabb.maxZ),
  );
}

function axisOverlapLength(aMin: number, aMax: number, bMin: number, bMax: number): number {
  return Math.max(0, Math.min(aMax, bMax) - Math.max(aMin, bMin));
}

function addFoundationAdjacenciesForFinishEdge(
  newEntities: RWTable<BuildingAnalyticEntity>,
  analyticEntities: Table<BuildingAnalyticEntity>,
  breps: RWTable<BRep>,
  edgeRef: BRepAdjacencyEdgeRef,
  coveredIntervals: readonly Interval[],
  options: { readonly distanceTolerance?: number; readonly candidateLengthThreshold?: number },
): void {
  const entity = analyticEntities[edgeRef.id];
  if (!isWallExteriorFinishBRep(entity)) {
    return;
  }

  const brep = breps[edgeRef.id];
  if (!brep) {
    return;
  }

  const face = brep.faces[edgeRef.faceIndex];
  if (!face) {
    return;
  }

  const edgeSegment = faceEdgeLine3Segment(face, edgeRef.edgeIndex, edgeRef.loopIndex);
  if (!edgeSegment) {
    return;
  }

  const { distanceTolerance = EDGE_OVERLAP_TOLERANCE, candidateLengthThreshold = EDGE_LENGTH_THRESHOLD } = options;

  // TODO(walls-v2): Remove this foundation fallback when wall/foundation associations are explicit.
  for (const { t0, t1 } of openEdgeIntervals(coveredIntervals, distanceTolerance)) {
    const openSegment = line3SegmentAtParams(edgeSegment, t0, t1);
    if (line3Len(...openSegment) <= candidateLengthThreshold) {
      continue;
    }

    const foundationEntity = findBestNearbyFoundation(openSegment, analyticEntities);
    if (!foundationEntity) {
      continue;
    }

    const parents = [entity.docEntityHandle, foundationEntity.docEntityHandle];

    addAnalyticLineSegment(
      "ANALYTIC_EXTERIOR_FINISH_EDGE",
      newEntities,
      "Exterior Finish Edge",
      parents,
      openSegment,
      [EXTERIOR_FINISH_TAG, EDGE],
      adjacencyStyle,
    );
  }
}

function faceAreaPredicate(face: Face): boolean {
  return Math.abs(faceArea(face)) >= MIN_FACE_AREA_THRESHOLD;
}

function addFinishCandidate(
  breps: RWTable<BRep>,
  candidates: BRepAdjacencyEdgeRef[],
  entity: BuildingAnalyticBRep,
): void {
  const { brep } = entity;
  const primaryFace = brep.faces[0];
  const primaryLoop = primaryFace?.loops[0];
  if (!primaryFace || !primaryLoop || !faceAreaPredicate(primaryFace)) {
    return;
  }

  breps[entity.id] = brep;

  const addCandidateEdge = (faceIndex: number, loopIndex: number, edgeIndex: number): void => {
    const face = brep.faces[faceIndex];
    const loop = face?.loops[loopIndex];
    if (!face || !loop || edgeIndex < 0 || edgeIndex >= polyLine2EdgeCount(loop)) {
      return;
    }

    candidates.push({
      id: entity.id,
      faceIndex,
      loopIndex,
      edgeIndex,
    });
  };

  const tolerance = EDGE_OVERLAP_TOLERANCE;

  const capFaces = brep.faces.slice(1);

  // If there are no cap faces, collect every face loop so zero-thickness opening perimeters participate.
  if (capFaces.length === 0) {
    traverseFaceEdges(primaryFace, (loopIndex, edgeIndex) => addCandidateEdge(0, loopIndex, edgeIndex));
    return;
  }

  const capLoopsInPrimaryPlane = capFaces.flatMap((capFace) => {
    const capLoop = capFace.loops[0];
    if (!capLoop) {
      return [];
    }

    return [planeReProjectPolyLine2(capLoop, capFace.plane, primaryFace.plane)];
  });

  // Primary loop edges that do not overlap with any cap loop in the primary plane are candidates.
  for (let edgeIndex = 0; edgeIndex < polyLine2EdgeCount(primaryLoop); edgeIndex++) {
    const edge = polyLine2Edge(primaryLoop, edgeIndex);
    if (!capLoopsInPrimaryPlane.some((capLoop) => polyLine2EdgesOverlap(edge, capLoop))) {
      addCandidateEdge(0, 0, edgeIndex);
    }
  }

  // Find cap edge candidates that lie on a plane approximating the substrate plane by
  // finding the cap depth from the primary face.
  const substratePlaneOffset = firstNonZeroCapFaceVertexOffset(primaryFace, capFaces, tolerance);
  if (substratePlaneOffset === undefined) {
    return;
  }

  const substratePlane = planeCopy(primaryFace.plane);
  planeMoveAlongNormal(substratePlane, substratePlaneOffset);

  for (let faceIndex = 1; faceIndex < brep.faces.length; faceIndex++) {
    const capFace = brep.faces[faceIndex];
    const capLoop = capFace.loops[0];
    if (!capLoop) {
      continue;
    }

    const loop3 = polyLine2OnPlane(capFace.plane, capLoop);
    for (const capEdgeIndex of planeContainsPolyLine3Edges(substratePlane, loop3, tolerance)) {
      addCandidateEdge(faceIndex, 0, capEdgeIndex);
    }
  }
}

function addOpeningCandidate(
  breps: RWTable<BRep>,
  candidates: BRepAdjacencyEdgeRef[],
  entity: BuildingAnalyticFace,
): void {
  if (!faceAreaPredicate(entity.face)) {
    return;
  }

  const brep = brepNew([entity.face], [faceTopoNew()]);
  breps[entity.id] = brep;

  traverseFaceEdges(entity.face, (loopIndex, edgeIndex) => {
    candidates.push({
      id: entity.id,
      faceIndex: 0,
      loopIndex,
      edgeIndex,
    });
  });
}

interface CurvedProfileAdjacencyPart {
  readonly adjacency: BRepAdjacency;
  readonly source: BRepAdjacencyCandidate;
  readonly segment: Line3Segment;
  readonly sourceT0: number;
  readonly side: ExteriorFinishEdgeOpeningSide | undefined;
}

function collectCurvedProfileEdgeKeys(
  analyticEntities: Table<BuildingAnalyticEntity>,
  edgeRefs: readonly BRepAdjacencyEdgeRef[],
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const edgeRef of edgeRefs) {
    const entity = analyticEntities[edgeRef.id];
    if (
      entity?.geometryType === "FACE" &&
      entity.loopEdgeTypes?.[edgeRef.loopIndex]?.[edgeRef.edgeIndex] === "CURVED"
    ) {
      keys.add(edgeCandidateKey(edgeRef));
    }
  }
  return keys;
}

function addEdgeAdjacenciesCurved(
  newEntities: RWTable<BuildingAnalyticEntity>,
  analyticEntities: Table<BuildingAnalyticEntity>,
  curvedParts: readonly CurvedProfileAdjacencyPart[],
): void {
  // Group curved parts by source edge and sort them by edge index and source parameter.
  const curvedPartCountBySourceEdge = new Map<string, number>();
  const curvedPartsByGroup = new Map<string, CurvedProfileAdjacencyPart[]>();
  for (const part of curvedParts) {
    const key = edgeCandidateKey(part.source);
    curvedPartCountBySourceEdge.set(key, (curvedPartCountBySourceEdge.get(key) ?? 0) + 1);
  }

  for (const discoveredPart of curvedParts) {
    const sourceEdgeKey = edgeCandidateKey(discoveredPart.source);
    const curvedPart =
      curvedPartCountBySourceEdge.get(sourceEdgeKey) === 1
        ? {
            ...discoveredPart,
            segment: discoveredPart.source.segment3,
            sourceT0: 0,
          }
        : discoveredPart;
    const adjacency = curvedPart.adjacency;
    const other = curvedPart.source === adjacency.candidateA ? adjacency.candidateB : adjacency.candidateA;
    const groupKey = [
      curvedPart.source.id,
      curvedPart.source.faceIndex,
      curvedPart.source.loopIndex,
      other.id,
      other.faceIndex,
      other.loopIndex,
      curvedPart.side ?? "",
    ].join(":");
    const group = curvedPartsByGroup.get(groupKey) ?? [];
    group.push(curvedPart);
    curvedPartsByGroup.set(groupKey, group);
  }

  // Sort each group by edge index and source parameter, then split into runs of connected parts and add them as curved edges.
  for (const group of curvedPartsByGroup.values()) {
    group.sort((a, b) => a.source.edgeIndex - b.source.edgeIndex || a.sourceT0 - b.sourceT0);
    const runs: CurvedProfileAdjacencyPart[][] = [];
    let run: CurvedProfileAdjacencyPart[] = [];
    for (const part of group) {
      const previous = run[run.length - 1];
      if (previous && !curvedProfilePartsConnect(previous, part)) {
        runs.push(run);
        run = [];
      }
      run.push(part);
    }
    if (run.length > 0) {
      runs.push(run);
    }

    const firstRun = runs[0];
    const lastRun = runs[runs.length - 1];
    const firstPart = firstRun?.[0];
    const lastPart = lastRun?.[lastRun.length - 1];
    if (
      runs.length > 1 &&
      firstRun &&
      lastRun &&
      firstPart &&
      lastPart &&
      curvedProfilePartsConnectAcrossLoopBoundary(lastPart, firstPart)
    ) {
      runs[0] = [...lastRun, ...firstRun];
      runs.pop();
    }

    for (const curvedRun of runs) {
      addCurvedProfileRun(newEntities, analyticEntities, curvedRun);
    }
  }
}

function curvedProfileAdjacencyPart(
  analyticEntities: Table<BuildingAnalyticEntity>,
  adjacency: BRepAdjacency,
): CurvedProfileAdjacencyPart | null {
  const entityA = analyticEntities[adjacency.candidateA.id];
  const entityB = analyticEntities[adjacency.candidateB.id];
  assertIsNotNullOrUndefined(entityA);
  assertIsNotNullOrUndefined(entityB);

  const source = exteriorProfileCandidate(entityA, entityB, adjacency);
  if (!source) {
    return null;
  }
  const interval = adjacencyIntervalOnCandidate(source, adjacency.segment);
  if (!interval) {
    return null;
  }
  const hasOpening = isExteriorOpeningUnitAnalyticFace(entityA) || isExteriorOpeningUnitAnalyticFace(entityB);
  return {
    adjacency,
    source,
    segment: line3SegmentAtParams(source.segment3, interval.t0, interval.t1),
    sourceT0: interval.t0,
    side: hasOpening ? computeExteriorFinishEdgeOpeningSide(source.face, source.segment2) : undefined,
  };
}

function exteriorProfileCandidate(
  entityA: BuildingAnalyticEntity,
  entityB: BuildingAnalyticEntity,
  adjacency: BRepAdjacency,
): BRepAdjacencyCandidate | undefined {
  if (isExteriorOpeningUnitAnalyticFace(entityA)) {
    return adjacency.candidateA;
  }
  if (isExteriorOpeningUnitAnalyticFace(entityB)) {
    return adjacency.candidateB;
  }
  if (isExteriorOpeningCasingClipsFinishAnalyticFace(entityA)) {
    return adjacency.candidateA;
  }
  if (isExteriorOpeningCasingClipsFinishAnalyticFace(entityB)) {
    return adjacency.candidateB;
  }
  return undefined;
}

function curvedProfilePartsConnect(previous: CurvedProfileAdjacencyPart, current: CurvedProfileAdjacencyPart): boolean {
  const sameEdge = previous.source.edgeIndex === current.source.edgeIndex;
  const nextEdge = current.source.edgeIndex === previous.source.edgeIndex + 1;
  if (!sameEdge && !nextEdge) {
    return false;
  }
  if (nextEdge) {
    return true;
  }
  // One source edge can produce multiple adjacency parts where it overlaps several finish edges
  // so we need to do a distance comparison.
  //
  // See these examples:
  // Source edge:  0 ───────────────────────── 10
  // Finish edges: 0 ───── 4   4 ───── 7   7 ───── 10
  // Parts on the same source edge should connect only when they touch; don't bridge a gap:
  // Source edge:  0 ───────────────────────── 10
  // Finish edges: 0 ───── 4       6 ─────── 10
  return (
    vec3DistSquared(
      previous.segment[3],
      previous.segment[4],
      previous.segment[5],
      current.segment[0],
      current.segment[1],
      current.segment[2],
    ) <= EDGE_OVERLAP_TOLERANCE_SQUARED
  );
}

function curvedProfilePartsConnectAcrossLoopBoundary(
  previous: CurvedProfileAdjacencyPart,
  current: CurvedProfileAdjacencyPart,
): boolean {
  const loop = previous.source.face.loops[previous.source.loopIndex];
  return (
    loop !== undefined && previous.source.edgeIndex === polyLine2EdgeCount(loop) - 1 && current.source.edgeIndex === 0
  );
}

function addCurvedProfileRun(
  newEntities: RWTable<BuildingAnalyticEntity>,
  analyticEntities: Table<BuildingAnalyticEntity>,
  run: readonly CurvedProfileAdjacencyPart[],
): void {
  const first = run[0];
  if (!first) {
    return;
  }
  const vertices = [first.segment[0], first.segment[1], first.segment[2]];
  for (let index = 0; index < run.length; index++) {
    const part = run[index];
    const next = run[index + 1];
    assertIsNotNullOrUndefined(part);
    if (next && next.source.edgeIndex === part.source.edgeIndex + 1) {
      vertices.push(part.source.segment3[3], part.source.segment3[4], part.source.segment3[5]);
    } else {
      vertices.push(part.segment[3], part.segment[4], part.segment[5]);
    }
  }
  addEdgeAdjacency(newEntities, analyticEntities, first.adjacency, "CURVED", first.side, polyLine3New(vertices));
}

function addEdgeAdjacency(
  newEntities: RWTable<BuildingAnalyticEntity>,
  analyticEntities: Table<BuildingAnalyticEntity>,
  adjacency: BRepAdjacency,
  geometryType: "STRAIGHT" | "CURVED",
  sideOverride?: ExteriorFinishEdgeOpeningSide,
  polyLine?: PolyLine3,
): void {
  const { candidateA, candidateB } = adjacency;
  const entityA = analyticEntities[candidateA.id];
  const entityB = analyticEntities[candidateB.id];
  assertIsNotNullOrUndefined(entityA);
  assertIsNotNullOrUndefined(entityB);

  const angleAndType = computeExteriorFinishEdgeAngleAndType(candidateA.face, candidateB.face);
  const side = sideOverride ?? exteriorFinishEdgeOpeningSide(entityA, entityB, adjacency);
  const attributeTags: BuildingGeomTag[] = [
    buildingGeomAttributeTagNew(EXTERIOR_FINISH_EDGE_GEOMETRY_TYPE_ATTRIBUTE, geometryType, "STRING"),
  ];
  if (angleAndType) {
    attributeTags.push(
      buildingGeomAttributeTagNew(EXTERIOR_FINISH_EDGE_ANGLE_ATTRIBUTE, angleAndType.angleDegrees, "NUMBER"),
      buildingGeomAttributeTagNew(EXTERIOR_FINISH_EDGE_TYPE_ATTRIBUTE, angleAndType.edgeType, "STRING"),
    );
  }
  if (side) {
    attributeTags.push(buildingGeomAttributeTagNew(EXTERIOR_FINISH_EDGE_SIDE_ATTRIBUTE, side, "STRING"));
  }

  const parents = [entityA.docEntityHandle, entityB.docEntityHandle];
  if (polyLine) {
    addAnalyticPolyLine(
      "ANALYTIC_EXTERIOR_FINISH_EDGE",
      newEntities,
      "Exterior Finish Edge",
      parents,
      polyLine,
      [EXTERIOR_FINISH_TAG, EDGE, ...attributeTags],
      adjacencyStyle,
    );
    return;
  }

  addAnalyticLineSegment(
    "ANALYTIC_EXTERIOR_FINISH_EDGE",
    newEntities,
    "Exterior Finish Edge",
    parents,
    adjacency.segment,
    [EXTERIOR_FINISH_TAG, EDGE, ...attributeTags],
    adjacencyStyle,
  );
}

function exteriorFinishEdgeOpeningSide(
  entityA: BuildingAnalyticEntity,
  entityB: BuildingAnalyticEntity,
  adjacency: BRepAdjacency,
): ExteriorFinishEdgeOpeningSide | undefined {
  const openingCandidate = exteriorOpeningCandidate(entityA, entityB, adjacency);
  if (!openingCandidate) {
    return undefined;
  }

  return computeExteriorFinishEdgeOpeningSide(openingCandidate.face, openingCandidate.segment2);
}

function exteriorOpeningCandidate(
  entityA: BuildingAnalyticEntity,
  entityB: BuildingAnalyticEntity,
  adjacency: BRepAdjacency,
): BRepAdjacencyCandidate | undefined {
  if (isExteriorOpeningUnitAnalyticFace(entityA)) {
    return adjacency.candidateA;
  }
  if (isExteriorOpeningUnitAnalyticFace(entityB)) {
    return adjacency.candidateB;
  }

  return undefined;
}

/** A wall mass should only have one thickness, so we can simply check for the first non-zero distance */
function firstNonZeroCapFaceVertexOffset(
  primaryFace: Face,
  capFaces: readonly Face[],
  tolerance: number,
): number | undefined {
  for (const capFace of capFaces) {
    const loop = capFace.loops[0];
    if (!loop) {
      continue;
    }

    for (let vertexIndex = 0; vertexIndex < polyLine2EdgeCount(loop); vertexIndex++) {
      const vertexOffset = vertexIndex * 2;
      const [x, y, z] = planeEval(capFace.plane, loop[vertexOffset], loop[vertexOffset + 1]);
      const planeOffset = planeSignedDistanceTo(primaryFace.plane, x, y, z);
      if (Math.abs(planeOffset) > tolerance) {
        return planeOffset;
      }
    }
  }

  return undefined;
}

export default memo(select, generate, compare);
